import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { previewCross, type CrossGeometry, type MatchState, type SimConfig } from '@orbomb/sim';
import { FinaleClock, finaleShownTick, totemFinale } from '../game/finale';
import { ArenaFrame, DESIGN_CELL, FRAME_REACH } from './frame';
import { blendPose, type Pose } from './motion';
import { campColor, campHot, PALETTE } from './palette';
import { peekBlockArt, peekOneUpArt } from './totemArt';
import { TotemRig } from './totemRig';

/** Totems rise this many cells past the playfield edge. */
const TOTEM_OVERHANG = 0.85;
/** Bonus medallion size relative to its pickup radius. */
const BONUS_ART_SCALE = 1.35;
/** Pop-in and pickup burst lengths, in milliseconds. */
const BONUS_POP_MS = 260;
const BONUS_BURST_MS = 650;
/** The medallion blinks over its last seconds on the field. */
const BONUS_BLINK_TICKS = 120;

interface BonusBurst {
  x: number;
  y: number;
  at: number;
}

export class ArenaView {
  readonly world = new Container();
  private readonly staticGfx = new Graphics();
  private readonly frameArt = new ArenaFrame();
  private readonly blockRoot = new Container();
  private readonly totemRoot = new Container();
  private readonly totems = {
    ORANGE: new TotemRig('ORANGE'),
    TURQUOISE: new TotemRig('TURQUOISE'),
  };
  private readonly bonusRoot = new Container();
  private readonly bonusGlow = new Graphics();
  private readonly bonusSprites = new Map<string, Sprite>();
  /** Last drawn bonuses, to tell a pickup from an expiry when one disappears. */
  private bonusSeen = new Map<
    string,
    { x: number; y: number; expiresAtTick: number; at: number }
  >();
  private bursts: BonusBurst[] = [];
  private readonly gfx = new Graphics();
  private ink: Graphics;
  private staticDirty = true;
  private cell = 32;
  private ox = 0;
  private oy = 0;
  /** ORANGE is drawn upside down so the guest sees their own ball at the bottom. */
  private facing: 'TURQUOISE' | 'ORANGE' = 'TURQUOISE';
  /** Online: draw this body from live state, not snapshot blend (avoids rubber-banding). */
  private liveBody: string | null = null;
  /** Poses before and after the latest tick. Drawing blends between them. */
  private from = new Map<string, Pose>();
  private to = new Map<string, Pose>();
  private blend = 1;
  private blastPreview = true;
  private readonly finale = new FinaleClock();

  constructor() {
    this.ink = this.gfx;
    this.world.eventMode = 'none';
    this.staticGfx.eventMode = 'none';
    this.blockRoot.eventMode = 'none';
    this.totemRoot.eventMode = 'none';
    this.bonusRoot.eventMode = 'none';
    this.bonusGlow.eventMode = 'none';
    this.gfx.eventMode = 'none';
    this.bonusRoot.addChild(this.bonusGlow);
    this.totemRoot.addChild(this.totems.ORANGE.view, this.totems.TURQUOISE.view);
    this.world.addChild(this.staticGfx);
    this.world.addChild(this.blockRoot);
    this.world.addChild(this.totemRoot);
    this.world.addChild(this.bonusRoot);
    this.world.addChild(this.gfx);
  }

  layout(width: number, height: number, columns: number, rows: number): void {
    const side = (FRAME_REACH.side / DESIGN_CELL) * 2;
    this.cell = Math.max(
      8,
      Math.min((width - 4) / (columns + side), (height - 8) / (rows + TOTEM_OVERHANG * 2)),
    );
    const bw = this.cell * columns;
    const bh = this.cell * rows;
    this.ox = (width - bw) / 2;
    this.oy = (height - bh) / 2;
    if (this.facing === 'ORANGE') {
      this.world.pivot.set(width / 2, height / 2);
      this.world.position.set(width / 2, height / 2);
      this.world.rotation = Math.PI;
    } else {
      this.world.pivot.set(0, 0);
      this.world.position.set(0, 0);
      this.world.rotation = 0;
    }
    this.staticDirty = true;
  }

  setFacing(camp: 'TURQUOISE' | 'ORANGE'): void {
    this.facing = camp;
  }

  setLiveBody(id: string | null): void {
    this.liveBody = id;
  }

  setBlastPreview(on: boolean): void {
    this.blastPreview = on;
  }

  /** Drop remembered poses so the next frame does not streak across a new match. */
  clearMotion(): void {
    this.from.clear();
    this.to.clear();
    this.bonusSeen.clear();
    this.bursts = [];
  }

  /** Call with the state from before this tick. */
  anchor(state: MatchState): void {
    if (this.to.size === 0) {
      const seed = this.capture(state);
      this.from = seed;
      this.to = new Map(seed);
      return;
    }
    this.from = this.to;
  }

  /** Call with the state from after this tick. */
  commit(state: MatchState): void {
    this.to = this.capture(state);
  }

  /** Keep this body at the committed pose so a snapshot does not smear it backward. */
  holdPose(id: string): void {
    const current = this.to.get(id);
    if (current) this.from.set(id, { x: current.x, y: current.y });
  }

  /** 0 draws the previous tick, 1 draws the tick just simulated. */
  setBlend(t: number): void {
    this.blend = t;
  }

  private capture(state: MatchState): Map<string, Pose> {
    const poses = new Map<string, Pose>();
    for (const player of state.players) poses.set(player.id, { x: player.x, y: player.y });
    for (const golden of state.goldens) poses.set(golden.id, { x: golden.x, y: golden.y });
    return poses;
  }

  private at(id: string, x: number, y: number): Pose {
    if (this.liveBody !== null && id === this.liveBody) return { x, y };
    const previous = this.from.get(id);
    const current = this.to.get(id);
    if (!previous || !current) return { x, y };
    return blendPose(previous, current, this.blend, this.liveBody !== null ? 4 : 1.25);
  }

  draw(state: MatchState, config: SimConfig, hitboxes: boolean, now = performance.now()): void {
    if (this.staticDirty) {
      this.ink = this.staticGfx;
      this.staticGfx.clear();
      this.frame(config);
      this.grid(config);
      this.frameArt.drawCorners();
      const bases = new Set(
        [...config.map.bases.ORANGE, ...config.map.bases.TURQUOISE].map(
          (cell) => `${cell.col},${cell.row}`,
        ),
      );
      this.clearBlocks();
      const blockArt = peekBlockArt();
      for (const block of config.map.blocks) {
        if (bases.has(`${block.col},${block.row}`)) continue;
        if (blockArt) this.stampBlock(block.col, block.row, blockArt);
        else this.block(block.col, block.row);
      }
      this.placeTotems(config);
      this.staticDirty = false;
    }
    this.totems.ORANGE.sync(state.totems[0].hp, now);
    this.totems.TURQUOISE.sync(state.totems[1].hp, now);

    this.ink = this.gfx;
    this.gfx.clear();

    const matchOver = state.clock.phase === 'ENDED';
    const finaleCause = matchOver ? state.result?.cause : null;
    const finaleElapsed = this.finale.elapsed(
      finaleCause,
      matchOver ? (state.result?.tick ?? null) : null,
      now,
    );
    const playingFinale = totemFinale(finaleCause);
    const shownTick = finaleShownTick(state.tick, playingFinale, finaleElapsed);
    for (const golden of state.goldens) {
      if (!this.blastPreview || matchOver || golden.status !== 'ARMED') continue;
      const remaining = (golden.detonationTick ?? state.tick) - state.tick;
      const urgent = remaining <= config.ticks.clearPreview;
      this.cross(
        previewCross(state, config, golden),
        urgent ? 0.38 : 0.14,
        urgent ? PALETTE.goldHot : PALETTE.preview,
        false,
        golden.id.length + state.tick,
      );
    }

    for (const explosion of state.explosions) {
      if (matchOver && !playingFinale) break;
      if (shownTick >= explosion.visualUntilTick) continue;
      const lethal = shownTick < explosion.lethalUntilTick;
      this.cross(
        explosion.cross,
        lethal ? 0.95 : 0.28,
        lethal ? PALETTE.goldHot : PALETTE.gold,
        lethal,
        explosion.id * 97 + Math.floor(shownTick),
      );
    }

    this.bonuses(state, config, matchOver, now);

    for (const golden of state.goldens) {
      if (golden.status === 'ABSENT') continue;
      const alpha = golden.status === 'MATERIALISING' ? 0.4 : 1;
      const armed = golden.status === 'ARMED';
      const pulse = armed ? 0.55 + 0.45 * Math.abs(Math.sin(state.tick * 0.12)) : 0.42;
      const shown = this.at(golden.id, golden.x, golden.y);
      this.marble(
        shown.x,
        shown.y,
        config.golden.radius,
        PALETTE.gold,
        PALETTE.goldHot,
        PALETTE.goldDeep,
        alpha,
        pulse,
      );
      if (armed && golden.detonationTick !== null) {
        const total = config.ticks.fuse;
        const left = Math.max(0, golden.detonationTick - state.tick);
        const color = golden.lastToucher ? campColor(golden.lastToucher) : PALETTE.gold;
        this.ring(shown.x, shown.y, config.golden.radius + 0.1, left / total, color);
      }
    }

    for (const player of state.players) {
      const stunned = player.status === 'STUNNED';
      const protectedWake = player.status === 'PROTECTED';
      const color = stunned ? PALETTE.stun : campColor(player.camp);
      const hot = stunned ? PALETTE.white : campHot(player.camp);
      const deep = stunned ? 0x3a3e44 : campColor(player.camp, true);
      const flash = protectedWake && Math.floor(state.tick / 6) % 2 === 0 ? 0.55 : 1;
      const shown = this.at(player.id, player.x, player.y);
      this.marble(
        shown.x,
        shown.y,
        config.player.radius,
        color,
        hot,
        deep,
        flash,
        stunned ? 0.12 : 0.55,
      );
      if (stunned) {
        const left = Math.max(0, player.stunUntilTick - state.tick);
        this.ring(
          shown.x,
          shown.y,
          config.player.radius + 0.1,
          left / config.ticks.stun,
          PALETTE.stun,
        );
      }
    }

    if (hitboxes) this.debugHitboxes(state, config);
  }

  /** 1Up medallions: glow and art under the marbles, gold countdown ring on top. */
  private bonuses(state: MatchState, config: SimConfig, matchOver: boolean, now: number): void {
    const glow = this.bonusGlow;
    glow.clear();
    const art = peekOneUpArt();
    const live = new Set<string>();
    const r = config.bonus.radius;

    for (const bonus of matchOver ? [] : state.bonuses) {
      live.add(bonus.id);
      const seen = this.bonusSeen.get(bonus.id);
      const bornAt = seen?.at ?? now;
      this.bonusSeen.set(bonus.id, {
        x: bonus.x,
        y: bonus.y,
        expiresAtTick: bonus.expiresAtTick,
        at: bornAt,
      });

      const pop = Math.min(1, (now - bornAt) / BONUS_POP_MS);
      // Ease-out-back: overshoots a little, then settles.
      const grow = 1 + 2.2 * (pop - 1) ** 3 + 1.2 * (pop - 1) ** 2;
      const left = Math.max(0, bonus.expiresAtTick - state.tick);
      const blinking = left <= BONUS_BLINK_TICKS;
      const blink = blinking
        ? 0.55 + 0.45 * Math.abs(Math.cos(now * (0.006 + (BONUS_BLINK_TICKS - left) * 0.0001)))
        : 1;
      const bob = Math.sin(now * 0.004) * 0.05;
      const px = this.sx(bonus.x);
      const py = this.sy(bonus.y + bob);
      const size = r * this.cell * BONUS_ART_SCALE * grow;

      const breathe = 0.75 + 0.25 * Math.sin(now * 0.005);
      glow.circle(px, this.sy(bonus.y) + r * this.cell * 0.85, r * this.cell * 0.8);
      glow.fill({ color: PALETTE.shadow, alpha: 0.35 * blink });
      glow.circle(px, py, size * 1.6 * breathe);
      glow.fill({ color: PALETTE.gold, alpha: 0.1 * blink });
      glow.circle(px, py, size * 1.15);
      glow.fill({ color: PALETTE.goldHot, alpha: 0.12 * blink });
      this.sparkles(glow, px, py, size * 1.35, now, blink);

      if (art) {
        let sprite = this.bonusSprites.get(bonus.id);
        if (!sprite) {
          sprite = new Sprite(art);
          sprite.anchor.set(0.5);
          sprite.eventMode = 'none';
          this.bonusSprites.set(bonus.id, sprite);
          this.bonusRoot.addChild(sprite);
        }
        sprite.position.set(px, py);
        sprite.width = size * 2;
        sprite.height = size * 2;
        sprite.alpha = blink;
        // Keep the art upright for the player who sees the board rotated.
        sprite.rotation = (this.facing === 'ORANGE' ? Math.PI : 0) + Math.sin(now * 0.003) * 0.08;
      } else {
        glow.circle(px, py, size);
        glow.fill({ color: PALETTE.gold, alpha: blink });
        glow.circle(px, py, size * 0.7);
        glow.fill({ color: 0xe0264f, alpha: blink });
      }

      const ringR = r * BONUS_ART_SCALE + 0.14;
      this.ink.circle(this.sx(bonus.x), py, ringR * this.cell);
      this.ink.stroke({ color: PALETTE.goldDeep, width: 3, alpha: 0.55 * blink });
      this.ringAt(this.sx(bonus.x), py, ringR, left / config.ticks.bonusLifetime, PALETTE.gold);
    }

    for (const [id, sprite] of this.bonusSprites) {
      if (live.has(id)) continue;
      sprite.destroy();
      this.bonusSprites.delete(id);
    }
    for (const [id, seen] of this.bonusSeen) {
      if (live.has(id)) continue;
      // Gone before its time: someone picked it up.
      if (!matchOver && state.tick < seen.expiresAtTick) {
        this.bursts.push({ x: seen.x, y: seen.y, at: now });
      }
      this.bonusSeen.delete(id);
    }
    this.bursts = this.bursts.filter((b) => now - b.at < BONUS_BURST_MS);
    for (const burst of this.bursts) this.burst(burst, r, now);
  }

  private sparkles(
    g: Graphics,
    px: number,
    py: number,
    radius: number,
    now: number,
    alpha: number,
  ) {
    for (let i = 0; i < 4; i++) {
      const angle = now * 0.0012 + (i * Math.PI) / 2;
      const twinkle = Math.max(0, Math.sin(now * 0.007 + i * 1.7));
      const x = px + Math.cos(angle) * radius;
      const y = py + Math.sin(angle) * radius;
      const s = this.cell * 0.07 * (0.4 + twinkle);
      g.poly([x, y - s, x + s * 0.28, y, x, y + s, x - s * 0.28, y]);
      g.fill({ color: PALETTE.goldHot, alpha: 0.9 * twinkle * alpha });
      g.poly([x - s, y, x, y - s * 0.28, x + s, y, x, y + s * 0.28]);
      g.fill({ color: PALETTE.goldHot, alpha: 0.9 * twinkle * alpha });
    }
  }

  /** Expanding gold shock ring and rising plus signs where a 1Up was collected. */
  private burst(burst: BonusBurst, radius: number, now: number): void {
    const g = this.ink;
    const t = (now - burst.at) / BONUS_BURST_MS;
    const fade = 1 - t;
    const px = this.sx(burst.x);
    const py = this.sy(burst.y);
    const r = radius * this.cell;
    g.circle(px, py, r * (1 + t * 3.2));
    g.stroke({ color: PALETTE.goldHot, width: 3 * fade + 1, alpha: 0.9 * fade });
    g.circle(px, py, r * (0.6 + t * 1.8));
    g.fill({ color: PALETTE.gold, alpha: 0.25 * fade });
    const rise = this.cell * 0.9 * t * (this.facing === 'ORANGE' ? -1 : 1);
    const arm = this.cell * 0.13;
    for (const dx of [-0.35, 0.35]) {
      const cx = px + dx * this.cell * (0.5 + t);
      const cy = py - rise - Math.abs(dx) * this.cell * 0.3;
      g.rect(cx - arm, cy - arm * 0.3, arm * 2, arm * 0.6);
      g.rect(cx - arm * 0.3, cy - arm, arm * 0.6, arm * 2);
      g.fill({ color: 0xff3d6a, alpha: fade });
    }
  }

  private sx(x: number): number {
    return this.ox + x * this.cell;
  }

  private sy(y: number): number {
    return this.oy + y * this.cell;
  }

  private frame(config: SimConfig): void {
    const cs = this.cell;
    this.frameArt.drawBase(this.ink, this.ox, this.oy, cs * config.columns, cs * config.rows, cs);
  }

  private corner(cx: number, cy: number, dx: number, dy: number): void {
    const g = this.ink;
    const s = this.cell * 0.22;
    g.moveTo(cx + dx * 2, cy + dy * s);
    g.lineTo(cx + dx * 2, cy + dy * 2);
    g.lineTo(cx + dx * s, cy + dy * 2);
    g.stroke({ color: PALETTE.boardEdge, width: 1.7, alpha: 0.88 });
  }

  private grid(config: SimConfig): void {
    const g = this.ink;
    const cs = this.cell;
    for (let col = 0; col < config.columns; col++) {
      for (let row = 0; row < config.rows; row++) {
        const inset = cs * 0.04;
        g.rect(this.sx(col) + inset, this.sy(row) + inset, cs - inset * 2, cs - inset * 2);
        g.fill({ color: PALETTE.cell, alpha: 0.28 });
      }
    }
    for (let x = 0; x <= config.columns; x++) {
      g.moveTo(this.ox + x * cs, this.oy);
      g.lineTo(this.ox + x * cs, this.oy + config.rows * cs);
    }
    for (let y = 0; y <= config.rows; y++) {
      g.moveTo(this.ox, this.oy + y * cs);
      g.lineTo(this.ox + config.columns * cs, this.oy + y * cs);
    }
    g.stroke({ color: PALETTE.grid, width: 1.05, alpha: 0.58 });
  }

  private clearBlocks(): void {
    const kids = this.blockRoot.removeChildren();
    for (const kid of kids) kid.destroy();
  }

  private stampBlock(col: number, row: number, texture: Texture): void {
    const pad = this.cell * 0.055;
    const sprite = new Sprite(texture);
    sprite.eventMode = 'none';
    sprite.roundPixels = false;
    sprite.x = this.sx(col) + pad;
    sprite.y = this.sy(row) + pad;
    sprite.width = this.cell - pad * 2;
    sprite.height = this.cell - pad * 2;
    this.blockRoot.addChild(sprite);
  }

  private block(col: number, row: number): void {
    const g = this.ink;
    const pad = this.cell * 0.08;
    const x = this.sx(col) + pad;
    const y = this.sy(row) + pad;
    const size = this.cell - pad * 2;
    const r = 5;

    g.roundRect(x + 2, y + 3, size, size, r);
    g.fill({ color: PALETTE.shadow, alpha: 0.45 });
    g.roundRect(x, y, size, size, r);
    g.fill({ color: PALETTE.block });
    g.roundRect(x, y, size, size * 0.38, r);
    g.fill({ color: PALETTE.blockTop, alpha: 0.7 });
    g.roundRect(x, y, size, size, r);
    g.stroke({ color: PALETTE.blockEdge, width: 1.8, alpha: 0.92 });

    const inset = 4;
    this.corner(x + inset, y + inset, 1, 1);
    this.corner(x + size - inset, y + inset, -1, 1);
    this.corner(x + inset, y + size - inset, 1, -1);
    this.corner(x + size - inset, y + size - inset, -1, -1);

    const cx = this.sx(col + 0.5);
    const cy = this.sy(row + 0.5);
    const gem = this.cell * 0.1;
    g.poly([cx, cy - gem, cx + gem * 0.7, cy, cx, cy + gem, cx - gem * 0.7, cy]);
    g.fill({ color: PALETTE.gem, alpha: 0.85 });
    g.poly([cx, cy - gem * 0.45, cx + gem * 0.28, cy, cx, cy + gem * 0.2, cx - gem * 0.28, cy]);
    g.fill({ color: PALETTE.white, alpha: 0.35 });
  }

  private placeTotems(config: SimConfig): void {
    for (const camp of ['ORANGE', 'TURQUOISE'] as const) {
      const cells = config.map.bases[camp];
      const minCol = Math.min(...cells.map((c) => c.col));
      const row = cells[0]?.row ?? 0;
      const toward: 1 | -1 = camp === 'ORANGE' ? 1 : -1;
      this.totems[camp].place(this.sx(minCol), this.sy(row), this.cell, toward);
    }
  }

  private marble(
    x: number,
    y: number,
    radius: number,
    color: number,
    highlight: number,
    deep: number,
    alpha: number,
    glow: number,
  ): void {
    const g = this.ink;
    const px = this.sx(x);
    const py = this.sy(y);
    const r = radius * this.cell;

    g.ellipse(px, py + r * 0.5, r * 0.92, r * 0.32);
    g.fill({ color: PALETTE.shadow, alpha: 0.48 * alpha });
    g.circle(px, py, r * (1.7 + glow * 0.28));
    g.fill({ color, alpha: 0.2 * glow * alpha });
    g.circle(px, py, r * 1.18);
    g.fill({ color, alpha: 0.14 * glow * alpha });
    g.circle(px, py, r);
    g.fill({ color: deep, alpha });
    g.circle(px, py - r * 0.06, r * 0.88);
    g.fill({ color, alpha });
    g.circle(px + r * 0.1, py + r * 0.2, r * 0.48);
    g.fill({ color: deep, alpha: 0.22 * alpha });
    g.ellipse(px - r * 0.24, py - r * 0.32, r * 0.4, r * 0.3);
    g.fill({ color: highlight, alpha: 0.58 * alpha });
    g.circle(px - r * 0.2, py - r * 0.38, r * 0.14);
    g.fill({ color: PALETTE.white, alpha: 0.78 * alpha });
  }

  private ring(x: number, y: number, radius: number, fraction: number, color: number): void {
    this.ringAt(this.sx(x), this.sy(y), radius, fraction, color);
  }

  private ringAt(px: number, py: number, radius: number, fraction: number, color: number): void {
    const g = this.ink;
    const r = radius * this.cell;
    const start = -Math.PI / 2;
    const end = start + Math.max(0.001, Math.min(1, fraction)) * Math.PI * 2;
    // moveTo the arc start so PixiJS does not stroke a line from the previous path.
    g.moveTo(px + Math.cos(start) * r, py + Math.sin(start) * r);
    g.arc(px, py, r, start, end);
    g.stroke({ color, width: 3, alpha: 0.95 });
  }

  private cross(
    cross: CrossGeometry,
    alpha: number,
    color: number,
    lethal: boolean,
    seed: number,
  ): void {
    const g = this.ink;
    const h = cross.horizontal;
    const v = cross.vertical;
    g.rect(this.sx(h.x0), this.sy(h.y0), (h.x1 - h.x0) * this.cell, (h.y1 - h.y0) * this.cell);
    g.fill({ color, alpha: alpha * (lethal ? 0.55 : 0.85) });
    g.rect(this.sx(v.x0), this.sy(v.y0), (v.x1 - v.x0) * this.cell, (v.y1 - v.y0) * this.cell);
    g.fill({ color, alpha: alpha * (lethal ? 0.55 : 0.85) });

    if (lethal) {
      const hy = this.sy((h.y0 + h.y1) / 2);
      const vx = this.sx((v.x0 + v.x1) / 2);
      g.rect(this.sx(h.x0), hy - 2, (h.x1 - h.x0) * this.cell, 4);
      g.fill({ color: PALETTE.white, alpha: 0.85 });
      g.rect(vx - 2, this.sy(v.y0), 4, (v.y1 - v.y0) * this.cell);
      g.fill({ color: PALETTE.white, alpha: 0.85 });
      this.lightning(this.sx(h.x0), hy, this.sx(h.x1), hy, seed, PALETTE.lightning);
      this.lightning(vx, this.sy(v.y0), vx, this.sy(v.y1), seed + 17, PALETTE.lightning);
      this.crossCore(cross);
    }
  }

  private lightning(x0: number, y0: number, x1: number, y1: number, seed: number, color: number) {
    const g = this.ink;
    const steps = 9;
    g.moveTo(x0, y0);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const n = this.noise(seed * 0.137 + i * 19.17);
      const offset = (n - 0.5) * this.cell * 0.38;
      const dx = x0 + (x1 - x0) * t;
      const dy = y0 + (y1 - y0) * t;
      const px = dx + (y1 === y0 ? 0 : offset);
      const py = dy + (x1 === x0 ? 0 : offset);
      g.lineTo(px, py);
    }
    g.stroke({ color, width: 1.4, alpha: 0.9 });
  }

  private noise(n: number): number {
    const x = Math.sin(n) * 43758.5453;
    return x - Math.floor(x);
  }

  private crossCore(cross: CrossGeometry): void {
    const g = this.ink;
    const cx = this.sx(cross.col + 0.5);
    const cy = this.sy(cross.row + 0.5);
    g.circle(cx, cy, this.cell * 0.7);
    g.fill({ color: PALETTE.goldHot, alpha: 0.28 });
    g.circle(cx, cy, this.cell * 0.22);
    g.fill({ color: PALETTE.white, alpha: 0.95 });
    g.circle(cx, cy, this.cell * 0.55);
    g.stroke({ color: PALETTE.goldHot, width: 2, alpha: 0.7 });
  }

  private debugHitboxes(state: MatchState, config: SimConfig): void {
    const g = this.ink;
    for (const rect of config.solids) {
      g.rect(
        this.sx(rect.x0),
        this.sy(rect.y0),
        (rect.x1 - rect.x0) * this.cell,
        (rect.y1 - rect.y0) * this.cell,
      );
      g.stroke({ color: 0x66ff99, width: 1, alpha: 0.7 });
    }
    for (const p of state.players) {
      g.circle(this.sx(p.x), this.sy(p.y), config.player.radius * this.cell);
      g.stroke({ color: 0xff6688, width: 1 });
    }
    for (const gold of state.goldens) {
      if (gold.status === 'ABSENT' || gold.status === 'MATERIALISING') continue;
      g.circle(this.sx(gold.x), this.sy(gold.y), config.golden.radius * this.cell);
      g.stroke({ color: 0xffee66, width: 1 });
    }
  }
}
